// src/pages/games/MemoryMatch/MemoryMatch.jsx
import React, { useState, useEffect } from 'react';
import { useGame } from '../../../contexts/GameContext';
import { useAuth } from '../../../contexts/AuthContext';
import { subscription } from '../../../services/api';

const THEMES = {
  ANIMALS: ['🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼'],
  FRUITS: ['🍎', '🍐', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓'],
  SPORTS: ['⚽', '🏀', '🏈', '⚾', '🎾', '🏐', '🏉', '🎱']
};

const MemoryMatch = () => {
  const { updateGameState } = useGame();
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [solved, setSolved] = useState([]);
  const [theme, setTheme] = useState('ANIMALS');
  const [moves, setMoves] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);

  // Initialize game
  useEffect(() => {
    initializeGame();
  }, [theme]);

  const initializeGame = () => {
    const symbols = [...THEMES[theme], ...THEMES[theme]];
    const shuffledCards = symbols
      .sort(() => Math.random() - 0.5)
      .map((symbol, index) => ({
        id: index,
        symbol,
        isFlipped: false,
        isSolved: false
      }));
    
    setCards(shuffledCards);
    setFlipped([]);
    setSolved([]);
    setMoves(0);
    setGameOver(false);
    setGameStarted(false);
    updateGameState({ moves: 0, matches: 0 });
  };

  const handleCardClick = (cardId) => {
    if (!gameStarted) {
      setGameStarted(true);
    }

    // Prevent clicking if two cards are already flipped
    if (flipped.length === 2) return;

    // Prevent clicking on already solved or flipped cards
    if (solved.includes(cardId) || flipped.includes(cardId)) return;

    const newFlipped = [...flipped, cardId];
    setFlipped(newFlipped);

    // Check for match if two cards are flipped
    if (newFlipped.length === 2) {
      setMoves(moves + 1);
      
      const [firstId, secondId] = newFlipped;
      const firstCard = cards.find(card => card.id === firstId);
      const secondCard = cards.find(card => card.id === secondId);

      if (firstCard.symbol === secondCard.symbol) {
        setSolved([...solved, firstId, secondId]);
        setFlipped([]);
        
        // Check if game is over
        if (solved.length + 2 === cards.length) {
          setGameOver(true);
          updateGameState({ moves, matches: cards.length / 2 });
        }
      } else {
        // Reset flipped cards after a delay
        setTimeout(() => {
          setFlipped([]);
        }, 1000);
      }
    }
  };

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold mb-2">Memory Match</h1>
          <div className="text-gray-600">Moves: {moves}</div>
        </div>
        <div className="flex gap-2">
          {Object.keys(THEMES).map((themeName) => (
            <button
              key={themeName}
              onClick={() => handleThemeChange(themeName)}
              className={`px-4 py-2 rounded ${
                theme === themeName
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              {themeName}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        {cards.map((card) => (
          <button
            key={card.id}
            onClick={() => handleCardClick(card.id)}
            className={`aspect-square text-4xl flex items-center justify-center rounded-lg transition-all duration-300 ${
              flipped.includes(card.id) || solved.includes(card.id)
                ? 'bg-white rotate-0'
                : 'bg-purple-500 rotate-180'
            }`}
            disabled={solved.includes(card.id)}
          >
            <span
              className={`transition-all duration-300 ${
                flipped.includes(card.id) || solved.includes(card.id)
                  ? 'rotate-0'
                  : 'rotate-180 opacity-0'
              }`}
            >
              {card.symbol}
            </span>
          </button>
        ))}
      </div>

      {gameOver && (
        <div className="text-center mt-6">
          <h2 className="text-xl font-bold mb-2">
            Congratulations! You won in {moves} moves!
          </h2>
          <button
            onClick={initializeGame}
            className="bg-purple-600 text-white px-6 py-2 rounded hover:bg-purple-700"
          >
            Play Again
          </button>
        </div>
      )}
    </div>
  );
};

export default MemoryMatch;